import React, { useEffect } from "react";

import Button from "@mui/material/Button";
import { CircularProgress, Divider, Step, StepButton, Stepper } from "@mui/material";
import useStyles from "./styles";

import localisations from "../localisation";
import { BadRequestError, ServerValidationErrors } from "components/ServerValidationErrors";
import { ResultStage } from "./stages";
import { ResultStageLocalisations } from "./stages/ResultStage";

export type WizardStep = {
    label: string;
    component: JSX.Element;
    onNext?: () => Promise<boolean>;
    disableNextButton?: boolean;
};

type WizardProps = {
    fieldCompanyId: number;
    steps: WizardStep[];
    resultStageLocalisations: ResultStageLocalisations & {
        validationErrorsMessage: string;
    };
    serverErrorLocalisations: any;
};

export default function Wizard(props: WizardProps) {
    const {
        buttons: { back, next, finish },
    } = localisations.general;

    const classes = useStyles();

    const [activeStepIndex, setActiveStepIndex] = React.useState(0);
    const [error, setError] = React.useState<Error | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [nextButtonIsDisabled, setNextButtonIsDisabled] = React.useState(false);

    let activeStepHasDisabledNextButton = props.steps[activeStepIndex]?.disableNextButton;

    useEffect(() => {
        setNextButtonIsDisabled(activeStepHasDisabledNextButton ?? false);
    }, [setNextButtonIsDisabled, activeStepHasDisabledNextButton]);

    const handleNext = async () => {
        const step = props.steps[activeStepIndex];

        if (step.onNext == null) {
            handleStep(activeStepIndex + 1);
            return;
        }

        setNextButtonIsDisabled(true);
        setIsSaving(true);
        
        try {
            if (await step.onNext()) {
                handleStep(activeStepIndex + 1);
            }
        } catch (err) {
            setError(err);
            if (!(err instanceof BadRequestError)) {
                handleStep(activeStepIndex + 1);
            }
        }

        setNextButtonIsDisabled(false);
        setIsSaving(false);
    };

    const handleBack = () => {
        handleStep(activeStepIndex - 1);
    };

    const handleStep = (stepIndex: number) => {
        if (stepIndex >= 0 && stepIndex <= props.steps.length && stepIndex !== activeStepIndex) {
            if (stepIndex !== props.steps.length) {
                setError(null);
            }
            setActiveStepIndex(stepIndex);
            window.location.hash = stepIndex.toString();
        }
    };

    const onHashChange = () => {
        const stepNumber = parseInt(decodeURI(window.location.hash).replace(/^#/, ""), 10);

        handleStep(isNaN(stepNumber) ? 0 : stepNumber);
    };

    const alert = error && (
        <ServerValidationErrors
            localisations={props.serverErrorLocalisations}
            title={props.resultStageLocalisations.validationErrorsMessage}
            error={error}
        />
    );

    // When this had a [] dependency, the activeStep seemed to get reset to 0.
    // What am I mis-understanding here ?
    useEffect(() => {      

        window.addEventListener("hashchange", onHashChange);
        
        return () => {
            window.removeEventListener("hashchange", onHashChange);
        };
    });

    return (
        <div className={classes.root}>
            <Stepper activeStep={activeStepIndex} alternativeLabel className={classes.stepper}>
                {props.steps.map((step, index) => {
                    const stepProps: { completed?: boolean } = {};
                    const buttonProps: { optional?: React.ReactNode } = {};

                    return (
                        <Step key={step.label} {...stepProps}>
                            <StepButton onClick={() => handleStep(index)} {...buttonProps}>
                                {step.label}
                            </StepButton>
                        </Step>
                    );
                })}
            </Stepper>
            {alert}
            {activeStepIndex === props.steps.length ? (
                <ResultStage isError={!!error} localisations={props.resultStageLocalisations} />
            ) : (
                <>
                    {props.steps[activeStepIndex].component}
                    <Divider className={classes.buttonDivider}></Divider>
                    <div className={classes.buttonBar}>
                        <Button disabled={activeStepIndex === 0} className={classes.backButton} onClick={handleBack}>
                            {back}
                        </Button>

                        <Button
                            disabled={nextButtonIsDisabled}
                            className={classes.nextButton}
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                        >
                            {isSaving && <CircularProgress color="inherit" size={20} aria-busy="true" />}
                            {activeStepIndex !== props.steps.length - 1 ? next : finish}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
